import {
  Entity,
  RELATION_HAS_MEMBER,
  UserEntity,
  parseEntityRef,
} from '@backstage/catalog-model';
import { CatalogApi, catalogApiRef } from '@backstage/plugin-catalog-react';
import { StringUtil } from '../../data-manipulation';
import { useApi } from '@backstage/core-plugin-api';

/**
 * Options for configuring user service behavior.
 */
export interface UserServiceOptions {
  /**
   * Whether to fetch the full entity details.
   * If `true`, retrieves the complete entity; otherwise, only selected fields.
   */
  fullEntity?: boolean;

  /**
   * Whether to include roles in the response for each user.
   */
  withRole?: boolean;
}

/**
 * Interface defining methods for interacting with user entities.
 */
export interface IUserEntityService {
  /**
   * Fetches a user entity by email address.
   *
   * @param email - The email address of the user.
   * @returns A promise resolving to the corresponding user entity.
   */
  byEmail: (email?: string) => Promise<UserEntity>;

  /**
   * Fetches user entities associated with a given entity.
   *
   * @param entity - The entity for which related users are being fetched.
   * @param options - Options to configure the response, such as including roles.
   * @returns A promise resolving to an array of extended user entities.
   */
  byEntity: (
    entity?: Entity,
    options?: UserServiceOptions,
  ) => Promise<ExtendedUserEntity[]>;
}

/**
 * Fields to fetch when querying for users by default.
 */
const userFields = [
  'metadata.name',
  'metadata.uid',
  'spec.profile',
  'metadata.namespace',
];

/**
 * Service class for interacting with user entities in the catalog.
 *
 * @category CatalogServices
 */
export class UserEntityService implements IUserEntityService {
  private catalogApi: CatalogApi;

  /**
   * Creates an instance of the UserEntityService.
   *
   * @param catalogApi - An instance of the Catalog API for querying entities.
   */
  constructor(catalogApi: CatalogApi) {
    this.catalogApi = catalogApi;
  }

  /**
   * Fetches user entities associated with a given entity.
   *
   * @param entity - The entity for which related users are being fetched.
   * @param options - Options to configure the response, such as including roles.
   * @returns A promise resolving to an array of extended user entities.
   */
  async byEntity(entity?: Entity, options?: UserServiceOptions) {
    if (!entity?.relations) {
      return [];
    }

    let userEntities: UserEntity[] = [];

    if (entity?.kind.toLowerCase() === 'group') {
      const groupMemberRelations = entity.relations.filter(
        relation => relation.type === RELATION_HAS_MEMBER,
      );

      const groupMemberNameFilters = groupMemberRelations.map(relation => ({
        kind: 'user',
        'metadata.name': parseEntityRef(relation.targetRef).name,
      }));

      const groupMembers = await this.catalogApi.getEntities({
        filter: groupMemberNameFilters,
        fields: options?.fullEntity ? undefined : userFields,
      });

      userEntities = groupMembers.items as UserEntity[];
    }

    if (entity?.kind.toLowerCase() === 'product') {
      const teamNames = entity?.spec?.teams as string[];
      if (!teamNames || !entity) return [];

      const teams =
        teamNames?.map(teamName => ({
          kind: 'group',
          'metadata.name': teamName,
        })) || [];

      const groups = await this.catalogApi.getEntities({
        filter: teams,
      });

      const members = groups.items.reduce<string[]>((acc, entity) => {
        const members = entity.relations?.filter(
          relation => relation.type === RELATION_HAS_MEMBER,
        );
        if (members) {
          members.forEach(member => {
            if (!acc.includes(member.targetRef)) {
              acc.push(member.targetRef);
            }
          });
        }
        return acc;
      }, []);

      const users = await this.catalogApi.getEntities({
        filter: members.map(member => ({
          kind: 'user',
          'metadata.name': parseEntityRef(member).name,
        })),
        fields: options?.fullEntity ? undefined : userFields,
      });

      userEntities = users.items as UserEntity[];

      if (options?.withRole) {
        const membersWithRoles = extractMembersWithRole(entity);

        userEntities = userEntities
          .map(member => {
            const userRole: string =
              membersWithRoles[member.spec?.profile?.email ?? ''] ??
              'softwareEngineer';
            const userMappedRole: RoleMapping = RoleMap[userRole] ?? {
              title: userRole,
              weight: 3,
            };

            return { ...member, role: userMappedRole };
          })
          .sort((a, b) => a.role.weight - b.role.weight);
      }
    }

    return userEntities as ExtendedUserEntity[];
  }

  /**
   * Fetches a user entity by email address.
   *
   * @param email - The email address of the user.
   * @returns A promise resolving to the corresponding user entity.
   */
  async byEmail(email?: string) {
    if (!email) return {} as UserEntity;
    const user = await this.catalogApi.getEntityByRef({
      kind: 'user',
      name: StringUtil.emailToName(email),
      namespace: 'default',
    });

    return user as UserEntity;
  }
}

/**
 * React hook for creating an instance of the `UserEntityService`.
 *
 * @returns {IUserEntityService} An instance of `UserEntityService` bound to the catalog API.
 *
 * @category CatalogServices
 */
export const useUserEntity = (): IUserEntityService => {
  return new UserEntityService(useApi(catalogApiRef));
};

/**
 * Extracts members and their associated roles from the entity's specification.
 *
 * @param productEntity - The product entity.
 * @returns A record mapping member emails to their roles.
 */
function extractMembersWithRole(productEntity: Entity): Record<string, string> {
  let membersWithRole: Record<string, string> = productEntity.spec
    ?.membersWithRole as Record<string, string>;

  if (productEntity.spec?.membersWithRole) {
    membersWithRole = Object.keys(membersWithRole).reduce((acc, key) => {
      return { ...acc, [membersWithRole[key]]: key };
    }, {}) as Record<string, string>;
  }
  return membersWithRole;
}

/**
 * Extended 'UserEntity' with an additional 'role' field to represent the user's role.
 */
export interface ExtendedUserEntity extends UserEntity {
  /**
   * The role associated with the user.
   */
  role: RoleMapping;
}

/**
 * Represents a role associated with a user, defining its title and priority weight.
 */
export type RoleMapping = {
  /**
   * The name or title of the role.
   */
  title: string;

  /**
   * The priority or significance of the role, where a higher weight indicates higher priority.
   */
  weight: number;
};

/**
 * RoleMap - Defines role metadata, including title and priority weight.
 */
const RoleMap: Record<string, RoleMapping> = {
  owner: { title: 'Owner', weight: 1 },
  leadEngineer: { title: 'Lead Engineer', weight: 2 },
  softwareEngineer: { title: 'Software Engineer', weight: 4 },
};
